import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Button,
  Spinner,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  FaPencilAlt,
  FaRegTrashAlt,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import {
  fetchDeliveryProfileById,
  fetchDeliveryHistory,
  deleteDeliveryProfile,
  approveDeliveryProfile,
  clearError,
  clearSuccess,
} from "../../redux/slices/deliveryProfileSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InfoPill from "../Common/InfoPill";
import reportService from "../../services/reportService";
import { FaFilePdf } from "react-icons/fa";
import {
  formatDateToDDMMYYYY,
  formatDateTimeToDDMMYYYY12Hr,
} from "../../utils/dateUtils";

const DeliveryProfileDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    currentDeliveryProfile,
    deliveryHistory,
    isLoading,
    isLoadingHistory,
    error,
    success,
    isDeleting,
    isUpdating,
  } = useSelector((state) => state.deliveryProfiles);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchDeliveryProfileById(id));
      dispatch(fetchDeliveryHistory({ id }));
    }
  }, [dispatch, id, success]);

  useEffect(() => {
    // Clear any existing success message when component mounts
    dispatch(clearSuccess());
  }, [dispatch]);

  // Clear messages and show toasts
  useEffect(() => {
    if (error) {
      toast.error(error);
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      const timer = setTimeout(() => dispatch(clearSuccess()), 5000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  // Cleanup effect to clear success message when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearSuccess());
    };
  }, [dispatch]);

  const handleEdit = () => {
    navigate(`/admin/delivery-profiles/edit/${id}`);
  };

  const handleDelete = () => {
    if (
      window.confirm("Are you sure you want to delete this delivery profile?")
    ) {
      dispatch(deleteDeliveryProfile(id));
      setTimeout(() => {
        navigate("/admin/delivery-profiles");
      }, 1000);
    }
  };

  const handleApprove = () => {
    setShowApproveModal(true);
  };

  const confirmApprove = () => {
    if (id) {
      dispatch(approveDeliveryProfile(id));
      setShowApproveModal(false);
    }
  };

  const handleBack = () => {
    navigate("/admin/delivery-profiles");
  };

  const handleDownloadPDF = async () => {
    try {
      setIsDownloading(true);
      const blob = await reportService.downloadDeliveryProfileHistoryPDF(
        id,
        {}
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `delivery-history-report-${
        currentDeliveryProfile?.first_name || "delivery"
      }-${currentDeliveryProfile?.last_name || "partner"}-${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  // Transform delivery history data to include searchable fields
  const transformedDeliveryHistory = useMemo(() => {
    if (!deliveryHistory || !Array.isArray(deliveryHistory)) {
      return [];
    }

    return deliveryHistory.map((order) => {
      // Create searchable customer name field
      const customerName =
        order.shipping_address?.receiver_name || "Unknown";
      const customerPhone = order.shipping_address?.receiver_phone || "";
      const customerSearchable = `${customerName} ${customerPhone}`.trim();

      // Create searchable items field with product names and quantities
      const formatItemQuantity = (item) => {
        if (
          item.order_quantity != null &&
          item.product_quantity != null
        ) {
          const unitDisplay = item.unit ? item.unit : "";
          return `${item.order_quantity} × ${item.product_quantity}${unitDisplay}`;
        }
        if (item.quantity != null) {
          const unitDisplay = item.unit ? ` ${item.unit}` : "";
          return `x${item.quantity}${unitDisplay}`;
        }
        return "";
      };

      const itemsSearchable = order.order_items
        ? order.order_items
            .map(
              (item) =>
                `${item.product_name || ""} ${formatItemQuantity(item)}`
            )
            .join(" ")
        : "";

      return {
        ...order,
        customer_name: customerSearchable,
        items_searchable: itemsSearchable,
      };
    });
  }, [deliveryHistory]);

  // Delivery history table headers
  const deliveryHistoryHeaders = [
    {
      prop: "id",
      title: "Order ID",
      isSortable: true,
      isFilterable: true,
      cell: (row) => <span className="fw-bold text-primary">{row.id}</span>,
    },
    {
      prop: "customer_name",
      title: "Customer",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <div>
          <div className="fw-bold">
            {row.shipping_address?.receiver_name || "Unknown"}
          </div>
          <small className="text-muted">
            {row.shipping_address?.receiver_phone || "N/A"}
          </small>
        </div>
      ),
    },
    {
      prop: "order_status",
      title: "Status",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Badge
          bg={
            row.order_status === "delivered"
              ? "success"
              : row.order_status === "cancelled"
              ? "danger"
              : row.order_status === "out_for_delivery"
              ? "warning"
              : "info"
          }
        >
          {row.order_status
            ?.replace(/_/g, " ")
            .split(" ")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(" ")}
        </Badge>
      ),
    },
    {
      prop: "payment_status",
      title: "Payment",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <Badge
          bg={
            row.payment_status === "paid" || row.payment_status === "completed"
              ? "success"
              : row.payment_status === "failed"
              ? "danger"
              : "warning"
          }
        >
          {row.payment_status?.charAt(0).toUpperCase() +
            row.payment_status?.slice(1).toLowerCase()}
        </Badge>
      ),
    },
    {
      prop: "final_amount",
      title: "Amount",
      isSortable: true,
      isFilterable: true,
      cell: (row) => (
        <div>
          <div className="fw-bold">₹{row.final_amount}</div>
          {row.delivery_charge > 0 && (
            <small className="text-muted">
              +₹{row.delivery_charge} delivery
            </small>
          )}
        </div>
      ),
    },
    {
      prop: "order_date",
      title: "Order Date",
      isFilterable: true,
      isSortable: true,
      cell: (row) => formatDateTimeToDDMMYYYY12Hr(row.order_date),
    },
    {
      prop: "items_searchable",
      isFilterable: true,
      title: "Items",
      cell: (row) => {
        const formatItemQuantity = (item) => {
          // If both order_quantity and product_quantity exist, show detailed format
          if (
            item.order_quantity != null &&
            item.product_quantity != null
          ) {
            // Include unit with product_quantity if available
            const unitDisplay = item.unit ? item.unit : "";
            return `${item.order_quantity} × ${item.product_quantity}${unitDisplay}`;
          }
          // Fallback to quantity if available
          if (item.quantity != null) {
            const unitDisplay = item.unit ? ` ${item.unit}` : "";
            return `x${item.quantity}${unitDisplay}`;
          }
          return "";
        };

        return (
          <div className="d-flex flex-wrap gap-1">
            {row.order_items && row.order_items.length > 0 ? (
              row.order_items.slice(0, 2).map((item, index) => (
                <Badge key={index} bg="secondary" className="me-1">
                  {item.product_name} ({formatItemQuantity(item)})
                </Badge>
              ))
            ) : (
              <span className="text-muted">No items</span>
            )}
            {row.order_items && row.order_items.length > 2 && (
              <Badge bg="light" text="dark">
                +{row.order_items.length - 2} more
              </Badge>
            )}
          </div>
        );
      },
    },
  ];
  // Helper function to render document preview (image or PDF)
  const renderDocumentPreview = (documentUrl, documentType, altText) => {
    if (!documentUrl) {
      return <div className="mt-2">-</div>;
    }

    const isPdf = documentUrl.toLowerCase().includes(".pdf");

    if (isPdf) {
      return (
        <div className="mt-2">
          <div
            style={{
              width: "100px",
              height: "100px",
              backgroundColor: "#f8f9fa",
              border: "2px solid #dc3545",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onClick={() => window.open(documentUrl, "_blank")}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#e9ecef";
              e.target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#f8f9fa";
              e.target.style.transform = "scale(1)";
            }}
          >
            <i className="fas fa-file-pdf fa-2x text-danger mb-2"></i>
            <span className="text-danger fw-bold" style={{ fontSize: "10px" }}>
              PDF
            </span>
          </div>
          {/* <div className="mt-2">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => window.open(documentUrl, '_blank')}
            >
              <i className="fas fa-external-link-alt me-1"></i>
              View PDF
            </Button>
          </div> */}
        </div>
      );
    } else {
      const isProfile = documentType === "profile_image";
      const imageStyle = isProfile
        ? {
            width: "170px",
            height: "170px",
            objectFit: "cover",
            borderRadius: "10%",
            boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
            cursor: "pointer",
            display: "block",
            margin: "0 auto",
          }
        : {
            width: "100px",
            height: "100px",
            objectFit: "cover",
            borderRadius: "8px",
            border: "2px solid #dee2e6",
            cursor: "pointer",
          };
      return (
        <div className="mt-2">
          <img
            src={documentUrl}
            alt={altText}
            style={imageStyle}
            onClick={() => window.open(documentUrl, "_blank")}
            onError={(e) => {
              console.log("Image load error:", documentUrl);
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "block";
            }}
          />
          <div className="text-muted mt-2" style={{ display: "none" }}>
            <i className="fas fa-image fa-2x mb-2"></i>
            <p>Failed to load image</p>
          </div>
        </div>
      );
    }
  };

  if (isLoading) {
    return (
      <Container className="view-form">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!currentDeliveryProfile) {
    return (
      <Container className="view-form">
        <div className="text-center mt-4">
          <h4>Delivery profile not found</h4>
          <Button variant="primary" onClick={handleBack}>
            Back to Delivery Profiles
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="view-form">
      <Row>
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">
              Delivery Profile Details
            </h5>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" size="sm" onClick={handleBack}>
                <i className="bi bi-arrow-left-short  fs-3"></i>
              </Button>
              {(() => {
                const isVerified =
                  currentDeliveryProfile?.is_verified === true ||
                  currentDeliveryProfile?.is_verified === "true";
                return (
                  !isVerified && (
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleApprove}
                      disabled={isUpdating}
                      title="Approve delivery partner"
                    >
                      {isUpdating ? "Approving..." : "Approve"}
                    </Button>
                  )
                );
              })()}
              <Button variant="outline-warning" size="sm" onClick={handleEdit}>
                <FaPencilAlt />
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                <FaRegTrashAlt />
              </Button>
            </div>
          </div>
          <Card className="shadow">
            <CardBody>
              <Row>
                <Col lg={2} className="bg-light rounded-4">
                  <Row>
                    <Col lg={12} className="my-2">
                      {/* <label className="fw-bold">Profile Image</label> */}
                      <div className="image-box">
                        {renderDocumentPreview(
                          currentDeliveryProfile?.image_url &&
                            currentDeliveryProfile.image_url.trim() !== ""
                            ? currentDeliveryProfile.image_url
                            : process.env.PUBLIC_URL + "/not-img-found.png", // ✅ Correct way
                          "profile_image",
                          `Profile Image - ${
                            currentDeliveryProfile?.first_name || ""
                          } ${currentDeliveryProfile?.last_name || ""}`
                        )}
                      </div>

                      <Col lg={12} className="my-2 mt-4 text-center">
                        <label className="fw-bold">
                          <h6>Full Name</h6>
                        </label>
                        <h5
                          className="mb-0 text-center"
                          style={{
                            textAlign: "left",
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "20px",
                          }}
                        >
                          {`${currentDeliveryProfile?.first_name || ""} ${
                            currentDeliveryProfile?.last_name || ""
                          }`.trim() || "-"}
                        </h5>
                      </Col>
                      <Col lg={12} className="my-2 text-center">
                        <label className="fw-bold">
                          <h6>Status</h6>
                        </label>
                        <h5
                          className="mb-0 text-center"
                          style={{
                            textAlign: "left",
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "20px",
                          }}
                        >
                          <Badge
                            bg={
                              currentDeliveryProfile?.is_active
                                ? "success"
                                : "secondary"
                            }
                          >
                            {currentDeliveryProfile?.is_active
                              ? "Active"
                              : "Inactive"}
                          </Badge>
                        </h5>
                      </Col>
                      <Col lg={12} className="my-2 text-center">
                        <label className="fw-bold">
                          <h6>Verification Status</h6>
                        </label>
                        <h5
                          className="mb-0 text-center"
                          style={{
                            textAlign: "left",
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "20px",
                          }}
                        >
                          {(() => {
                            const isVerified =
                              currentDeliveryProfile?.is_verified === true ||
                              currentDeliveryProfile?.is_verified === "true";
                            return (
                              <Badge
                                bg={isVerified ? "success" : "warning"}
                                className="d-flex align-items-center justify-content-center gap-2"
                                style={{
                                  width: "fit-content",
                                  margin: "0 auto",
                                }}
                              >
                                {isVerified ? (
                                  <>
                                    <FaCheckCircle /> Verified
                                  </>
                                ) : (
                                  <>
                                    <FaTimesCircle /> Pending Approval
                                  </>
                                )}
                              </Badge>
                            );
                          })()}
                        </h5>
                      </Col>
                    </Col>
                  </Row>
                </Col>
                <Col lg={10}>
                  {/* Personal Information Section */}
                  <Row className="mx-3 my-2 view-form">
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Gender</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentDeliveryProfile?.gender || "-"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Email</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentDeliveryProfile?.email || "-"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Phone</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentDeliveryProfile?.phone || "-"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Hire Date</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentDeliveryProfile?.hire_date
                          ? formatDateToDDMMYYYY(
                              currentDeliveryProfile.hire_date
                            )
                          : "-"}
                      </p>
                    </Col>

                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Address</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentDeliveryProfile?.address ||
                          "No address provided"}
                      </p>
                    </Col>

                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Country</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentDeliveryProfile?.country || "-"}
                      </p>
                    </Col>
                  </Row>

                  {/* Address Information Section */}
                  <Row className="mx-3 my-2 view-form">
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">State</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentDeliveryProfile?.state || "-"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">City</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentDeliveryProfile?.city || "-"}
                      </p>
                    </Col>
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Pincode</label>
                      <p
                        className="mb-0"
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "5px",
                        }}
                      >
                        {currentDeliveryProfile?.pincode || "-"}
                      </p>
                    </Col>
                  </Row>

                  {/* Document Information Section */}
                  <Row className="mx-3 my-2 view-form">
                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Aadhar Card</label>
                      <div
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "15px",
                        }}
                      >
                        {renderDocumentPreview(
                          currentDeliveryProfile?.aadhar_card,
                          "aadhar_card",
                          `Aadhar Card - ${currentDeliveryProfile?.first_name} ${currentDeliveryProfile?.last_name}`
                        )}
                      </div>
                    </Col>

                    <Col lg={4} className="my-2">
                      <label className="fw-bold">PAN Card</label>
                      <div
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "15px",
                        }}
                      >
                        {renderDocumentPreview(
                          currentDeliveryProfile?.pan_card,
                          "pan_card",
                          `PAN Card - ${currentDeliveryProfile?.first_name} ${currentDeliveryProfile?.last_name}`
                        )}
                      </div>
                    </Col>

                    <Col lg={4} className="my-2">
                      <label className="fw-bold">Driving Licence</label>
                      <div
                        style={{
                          textAlign: "left",
                          borderBottom: "1px solid #ccc",
                          paddingBottom: "15px",
                        }}
                      >
                        {renderDocumentPreview(
                          currentDeliveryProfile?.driving_licence,
                          "driving_licence",
                          `Driving Licence - ${currentDeliveryProfile?.first_name} ${currentDeliveryProfile?.last_name}`
                        )}
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Delivery History Section */}
      <Row className="mt-4">
        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">
              Delivery History ({deliveryHistory?.length || 0})
            </h5>
            <Button
              variant="danger"
              className="report-btn-download"
              onClick={handleDownloadPDF}
              disabled={
                isDownloading ||
                isLoadingHistory ||
                deliveryHistory?.length === 0
              }
            >
              <FaFilePdf /> Download PDF
            </Button>
          </div>
          <Card className="shadow">
            <CardBody>
              {isLoadingHistory ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-2">Loading delivery history...</p>
                </div>
              ) : deliveryHistory && deliveryHistory.length > 0 ? (
                <DatatableWrapper
                  body={transformedDeliveryHistory}
                  headers={deliveryHistoryHeaders}
                  paginationOptionsProps={{
                    initialState: {
                      rowsPerPage: 5,
                      options: [5, 10, 15, 20],
                    },
                  }}
                >
                  {/* Controls */}
                  <div className="d-flex align-items-center justify-content-start flex-wrap gap-2 mb-3">
                    <div className="flex-grow-1" style={{ maxWidth: "300px" }}>
                      <Filter
                        placeholder="Search orders..."
                        className="w-100"
                      />
                    </div>
                    <PaginationOptions />
                    <InfoPill left="Total" right={transformedDeliveryHistory?.length} />
                  </div>

                  {/* Table */}
                  <div className="table-responsive shadow-sm">
                    <table className="table table-striped table-bordered align-middle">
                      <TableHeader />
                      <TableBody />
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-start mt-3">
                    <Pagination classes={{
                      button: "bg-dark",
                    }} />
                  </div>
                </DatatableWrapper>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-truck fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No Delivery History Found</h5>
                  <p className="text-muted">
                    This delivery partner hasn't completed any deliveries yet.
                  </p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Approve Confirmation Modal */}
      <Modal
        show={showApproveModal}
        onHide={() => {
          setShowApproveModal(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Approve Delivery Partner</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to approve delivery partner{" "}
          <strong>
            {currentDeliveryProfile?.first_name}{" "}
            {currentDeliveryProfile?.last_name}
          </strong>
          ? They will be able to login after approval.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowApproveModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={confirmApprove}
            disabled={isUpdating}
          >
            {isUpdating ? "Approving..." : "Approve"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DeliveryProfileDetails;
